import { FC, useState, useEffect } from 'react';
import { Color } from 'src/utils/theme';
import { Icon } from 'src/components';
import { WordRowList } from 'src/apps/Generic/WordRowList';
import { defineMessages, useIntl } from 'react-intl';
import { Creator, LearnableProgress } from 'src/api/wordlists/types';
import { AudioProvider } from 'src/context/AudioContext';
import { SelectionState, SelectionActions } from 'src/types/selection';
import * as S from './styles';

const msgs = defineMessages({
  close: { id: 'scenario_dashboard_sidebar_collapse' },
  expand: { id: 'scenario_dashboard_sidebar_expand' },
  words: { id: 'wordlists_viewlist_section1_subheader' },
});

interface Props {
  title: string;
  words: LearnableProgress[];
  bgColor?: Color;
  expanded?: boolean;
  showOptions?: boolean;
  creator?: Creator;
  creatorId?: number | null;
  wordsCount: number;
  onToggleExpand?: (expanded: boolean) => void;
  hideDifficultToggle?: boolean;
  hideMarkAsKnownToggle?: boolean;
  refetchLearnables?: () => void;
  sectionType: 'learned' | 'toLearn';
  selectionState: SelectionState;
  selectionActions: SelectionActions;
  wordlistId?: string;
  showNextReview?: boolean;
}

export const WordsAccordion: FC<Props> = ({
  title,
  words,
  bgColor = 'neutral99',
  expanded = false,
  showOptions,
  creator,
  creatorId,
  wordsCount,
  onToggleExpand,
  hideDifficultToggle,
  hideMarkAsKnownToggle,
  refetchLearnables,
  sectionType,
  selectionState,
  selectionActions,
  wordlistId,
  showNextReview,
}) => {
  const [isExpanded, setIsExpanded] = useState(expanded);
  const intl = useIntl();

  useEffect(() => {
    setIsExpanded(expanded);
  }, [expanded]);

  useEffect(() => {
    onToggleExpand?.(isExpanded);
  }, [isExpanded, onToggleExpand]);

  return (
    <S.Container $bgColor={bgColor}>
      <S.Header $bgColor={bgColor} onClick={() => setIsExpanded(!isExpanded)}>
        <div>
          <S.Title>{title}</S.Title>
          <S.Count>
            {intl.formatMessage(msgs.words, {
              n: wordsCount,
            })}
          </S.Count>
        </div>
        {wordsCount > 0 && (
          <S.IconWrapper
            $isExpanded={isExpanded}
            aria-label={
              isExpanded ? intl.formatMessage(msgs.close) : intl.formatMessage(msgs.expand)
            }
          >
            <Icon name="down" />
          </S.IconWrapper>
        )}
      </S.Header>
      {isExpanded && (
        <AudioProvider>
          <WordRowList
            items={words.map(word => ({
              id: word.learnableIdKey,
              targetValue: word.displayTargetItem,
              sourceValue: word.sourceDefinitionValue,
              audioUrl: word.audios[1]?.url,
              difficult: word.markedAsDifficult,
              known: word.markedAsKnown || word.isLearned,
              wordlistCreatorId: creatorId,
              nextReview: showNextReview && !word.markedAsKnown ? word.nextReview : null,
            }))}
            showOptions={showOptions}
            wordlistCreator={creator}
            hideDifficultToggle={hideDifficultToggle}
            hideMarkAsKnownToggle={hideMarkAsKnownToggle}
            refetchLearnables={refetchLearnables}
            sectionType={sectionType}
            selectionState={selectionState}
            selectionActions={selectionActions}
            wordlistId={wordlistId}
          />
        </AudioProvider>
      )}
    </S.Container>
  );
};
